import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import Tooltip from '../ui/Tooltip';

// One notice mechanism for the console. Success notices are polite (role="status")
// and dismiss themselves after SUCCESS_MS; errors are assertive (role="alert") and
// stay until dismissed. The live regions are always mounted so screen readers
// announce notices that are added to them.
const SUCCESS_MS = 6000;

const NoticeContext = createContext(null);

const noop = () => {};
const fallback = { success: noop, error: noop, dismiss: noop, clearErrors: noop };

export const useNotice = () => useContext(NoticeContext) || fallback;

const errorText = (err, fallbackText) => {
    if (typeof err === 'string') return err;
    return err?.response?.data?.message || err?.message || fallbackText || 'Something went wrong.';
};

const Notice = ({ notice, onDismiss }) => {
    const timer = useRef(null);
    const isSuccess = notice.tone === 'success';

    const start = useCallback(() => {
        if (!isSuccess) return;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => onDismiss(notice.id), SUCCESS_MS);
    }, [isSuccess, notice.id, onDismiss]);
    const pause = () => clearTimeout(timer.current);

    useEffect(() => {
        start();
        return () => clearTimeout(timer.current);
    }, [start]);

    const Icon = isSuccess ? CheckCircle2 : AlertCircle;
    return (
        <div
            onMouseEnter={pause}
            onMouseLeave={start}
            onFocus={pause}
            onBlur={start}
            className={`pointer-events-auto flex items-start gap-3 rounded-xl border bg-white px-4 py-3 text-sm shadow-lg ${
                isSuccess ? 'border-emerald-200' : 'border-red-200'
            }`}
        >
            <Icon
                className={`mt-0.5 h-4 w-4 shrink-0 ${isSuccess ? 'text-emerald-600' : 'text-red-600'}`}
                aria-hidden="true"
            />
            <p className="min-w-0 flex-1 break-words text-slate-800">{notice.message}</p>
            <Tooltip label="Dismiss">
                <button
                    type="button"
                    onClick={() => onDismiss(notice.id)}
                    aria-label="Dismiss notification"
                    className="-mr-1 -mt-0.5 rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </Tooltip>
        </div>
    );
};

export const NoticeProvider = ({ children }) => {
    const [notices, setNotices] = useState([]);
    const nextId = useRef(0);

    const dismiss = useCallback((id) => setNotices((list) => list.filter((n) => n.id !== id)), []);

    const push = useCallback((tone, message) => {
        if (!message) return;
        setNotices((list) => {
            // Re-announce a repeated message instead of stacking duplicates.
            const rest = list.filter((n) => !(n.tone === tone && n.message === message));
            nextId.current += 1;
            return [...rest, { id: nextId.current, tone, message }].slice(-5);
        });
    }, []);

    const success = useCallback((message) => push('success', message), [push]);
    const error = useCallback((err, fallbackText) => push('error', errorText(err, fallbackText)), [push]);
    const clearErrors = useCallback(() => setNotices((list) => list.filter((n) => n.tone !== 'error')), []);

    const api = useMemo(() => ({ success, error, dismiss, clearErrors }), [success, error, dismiss, clearErrors]);

    const successes = notices.filter((n) => n.tone === 'success');
    const errors = notices.filter((n) => n.tone === 'error');

    return (
        <NoticeContext.Provider value={api}>
            {children}
            <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-stretch gap-2 sm:left-auto sm:w-96">
                <div role="alert" aria-live="assertive" className="flex flex-col gap-2">
                    {errors.map((n) => (
                        <Notice key={n.id} notice={n} onDismiss={dismiss} />
                    ))}
                </div>
                <div role="status" aria-live="polite" className="flex flex-col gap-2">
                    {successes.map((n) => (
                        <Notice key={n.id} notice={n} onDismiss={dismiss} />
                    ))}
                </div>
            </div>
        </NoticeContext.Provider>
    );
};
